import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import { Income } from "../../lib/types";
import { toLocalDatetimeInputValue } from "../../lib/dateUtils";
import CreatedViaBadge from "../common/CreatedViaBadge";
import {
  integrationsAPI,
  ForgeOrganization,
  ForgeProject,
  BillingDocument,
} from "../../services/api/api";

interface IncomeFormProps {
  onAddIncome: (income: Omit<Income, "id">) => Promise<void> | void;
  onUpdateIncome?: (income: Income) => Promise<void> | void;
  incomeToEdit?: Income;
  isEditing?: boolean;
}

const CLIENT_STORAGE_KEY = "income_client_names_v1";
const DAILY_RECURRENCE_VALUE = "daily";

const recurrenceOptions = [
  { value: "", label: "One-time" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "bi-weekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const recurrenceDayOptions = [
  { label: "M", value: "mon" },
  { label: "T", value: "tue" },
  { label: "We", value: "wed" },
  { label: "Thu", value: "thu" },
  { label: "Fri", value: "fri" },
  { label: "Sat", value: "sat" },
  { label: "Sun", value: "sun" },
];

const normalizeDateTimeInput = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T00:01`;
  }
  return trimmed;
};

const parseDateInput = (value: string) => {
  if (!value) return undefined;
  const parsed = new Date(normalizeDateTimeInput(value));
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed;
};

const IncomeForm: React.FC<IncomeFormProps> = ({
  onAddIncome,
  onUpdateIncome,
  incomeToEdit,
  isEditing = false,
}) => {
  const [client, setClient] = useState(incomeToEdit?.client || "");
  const [clientOptions, setClientOptions] = useState<string[]>([]);
  const [invoiceLink, setInvoiceLink] = useState(
    incomeToEdit?.invoiceLink || ""
  );
  const [invoiceTotal, setInvoiceTotal] = useState(
    incomeToEdit?.invoiceTotal?.toString() || ""
  );
  const [paidToDate, setPaidToDate] = useState(
    incomeToEdit?.paidToDate?.toString() || ""
  );
  const [paymentProcessorFee, setPaymentProcessorFee] = useState(
    (incomeToEdit?.paymentProcessorFee ?? 0).toString()
  );
  const [loanRepayment, setLoanRepayment] = useState(
    (incomeToEdit?.loanRepayment ?? 0).toString()
  );
  const [incomeType, setIncomeType] = useState<"Actual" | "Prospective">(
    incomeToEdit?.type === "Prospective" ? "Prospective" : "Actual"
  );
  const [recurrencePattern, setRecurrencePattern] = useState(() => {
    if (incomeToEdit?.recurrencePattern?.startsWith("daily:")) {
      return DAILY_RECURRENCE_VALUE;
    }
    return incomeToEdit?.recurrencePattern || "";
  });
  const [recurrenceDays, setRecurrenceDays] = useState<string[]>(() => {
    const pattern = incomeToEdit?.recurrencePattern;
    if (pattern?.startsWith("daily:")) {
      return pattern.replace("daily:", "").split(",").filter(Boolean);
    }
    return [];
  });
  const [expectedByDate, setExpectedByDate] = useState(
    incomeToEdit?.expectedByDate
      ? new Date(incomeToEdit.expectedByDate).toISOString().split("T")[0]
      : ""
  );
  const [receivedDate, setReceivedDate] = useState(
    incomeToEdit?.receivedDate
      ? toLocalDatetimeInputValue(new Date(incomeToEdit.receivedDate))
      : ""
  );
  const [depositDate, setDepositDate] = useState(
    incomeToEdit?.depositDate
      ? toLocalDatetimeInputValue(new Date(incomeToEdit.depositDate))
      : ""
  );
  const [dependsOnCompletion, setDependsOnCompletion] = useState(
    incomeToEdit?.dependsOnCompletion || ""
  );
  const [notes, setNotes] = useState(incomeToEdit?.notes || "");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialRecurrencePatternRef = useRef(
    incomeToEdit?.recurrencePattern || ""
  );

  // External integrations (Focus: Forge, TPC Account Center)
  const [forgeConnected, setForgeConnected] = useState(false);
  const [tpcConnected, setTpcConnected] = useState(false);
  const [forgeOrgs, setForgeOrgs] = useState<ForgeOrganization[]>([]);
  const [forgeProjects, setForgeProjects] = useState<ForgeProject[]>([]);
  // Every Forge project across all organizations, so the Project field is a
  // type-to-search even before a client/org has been picked.
  const [allForgeProjects, setAllForgeProjects] = useState<ForgeProject[]>([]);
  const [billingDocs, setBillingDocs] = useState<BillingDocument[]>([]);
  const [project, setProject] = useState(incomeToEdit?.project || "");
  const [forgeOrganizationId, setForgeOrganizationId] = useState(
    incomeToEdit?.forgeOrganizationId || ""
  );
  const [forgeProjectId, setForgeProjectId] = useState(
    incomeToEdit?.forgeProjectId || ""
  );
  const [selectedDocId, setSelectedDocId] = useState("");
  const [sourceProvider, setSourceProvider] = useState(
    incomeToEdit?.sourceProvider || ""
  );
  const [sourceDocumentType, setSourceDocumentType] = useState(
    incomeToEdit?.sourceDocumentType || ""
  );
  const [sourceDocumentId, setSourceDocumentId] = useState(
    incomeToEdit?.sourceDocumentId || ""
  );

  // Load connected integrations and their option lists.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await integrationsAPI.list();
        if (cancelled) return;
        const forge = list.some((i) => i.provider === "forge");
        const tpc = list.some((i) => i.provider === "tpc");
        setForgeConnected(forge);
        setTpcConnected(tpc);
        if (forge) {
          integrationsAPI
            .forgeOrganizations()
            .then((o) => !cancelled && setForgeOrgs(o))
            .catch(() => undefined);
          // Preload every project (no org filter) so the Project field can be
          // searched across all clients without first picking one.
          integrationsAPI
            .forgeProjects("")
            .then((p) => !cancelled && setAllForgeProjects(p))
            .catch(() => undefined);
        }
        if (tpc) {
          integrationsAPI
            .tpcBillingDocuments()
            .then((d) => !cancelled && setBillingDocs(d))
            .catch(() => undefined);
        }
      } catch {
        /* integrations are optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // When the chosen client matches a Forge organization, load its projects.
  useEffect(() => {
    if (!forgeConnected || forgeOrgs.length === 0) return;
    const match = forgeOrgs.find(
      (o) => o.name.toLowerCase() === client.trim().toLowerCase()
    );
    if (match) {
      setForgeOrganizationId(match.id);
      integrationsAPI
        .forgeProjects(match.id)
        .then(setForgeProjects)
        .catch(() => setForgeProjects([]));
    } else {
      setForgeOrganizationId("");
      setForgeProjects([]);
    }
  }, [client, forgeOrgs, forgeConnected]);

  // Client options = local saved names ∪ Forge organizations.
  const clientSelectOptions = useMemo(() => {
    const names = new Set<string>(clientOptions);
    forgeOrgs.forEach((o) => names.add(o.name));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [clientOptions, forgeOrgs]);

  useEffect(() => {
    const storedOptions = localStorage.getItem(CLIENT_STORAGE_KEY);
    const parsedOptions: string[] = storedOptions
      ? JSON.parse(storedOptions)
      : [];
    const merged = Array.from(
      new Set(
        [...parsedOptions, incomeToEdit?.client || ""]
          .map((name) => name.trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
    setClientOptions(merged);
  }, [incomeToEdit?.client]);

  const persistClientOptions = (nextOptions: string[]) => {
    const normalized = Array.from(
      new Set(nextOptions.map((name) => name.trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    setClientOptions(normalized);
    localStorage.setItem(CLIENT_STORAGE_KEY, JSON.stringify(normalized));
  };

  const clientTrimmed = client.trim();
  const canAddClient = useMemo(
    () =>
      Boolean(clientTrimmed) &&
      !clientOptions.some(
        (option) => option.toLowerCase() === clientTrimmed.toLowerCase()
      ),
    [clientOptions, clientTrimmed]
  );

  const handleAddClient = () => {
    if (!canAddClient) return;
    persistClientOptions([...clientOptions, clientTrimmed]);
    setClient(clientTrimmed);
  };

  // "+ create project": enabled when the typed project name doesn't already
  // exist (in the active source list) and a Forge client/org is selected so we
  // know where to create it.
  const projectTrimmed = project.trim();
  const projectSearchSource = forgeOrganizationId
    ? forgeProjects
    : allForgeProjects;
  const canAddProject = useMemo(
    () =>
      forgeConnected &&
      Boolean(forgeOrganizationId) &&
      Boolean(projectTrimmed) &&
      !projectSearchSource.some(
        (p) => p.name.toLowerCase() === projectTrimmed.toLowerCase()
      ),
    [forgeConnected, forgeOrganizationId, projectTrimmed, projectSearchSource]
  );
  const [creatingProject, setCreatingProject] = useState(false);

  const handleAddProject = async () => {
    if (!canAddProject || creatingProject) return;
    setCreatingProject(true);
    try {
      const created = await integrationsAPI.createForgeProject(
        projectTrimmed,
        forgeOrganizationId
      );
      setProject(created.name);
      setForgeProjectId(created.id);
      setForgeProjects((prev) =>
        prev.some((p) => p.id === created.id) ? prev : [...prev, created]
      );
      setAllForgeProjects((prev) =>
        prev.some((p) => p.id === created.id) ? prev : [...prev, created]
      );
    } catch (err) {
      // Surface the failure; the field stays as typed so the user can retry.
      console.error("Failed to create Forge project", err);
      window.alert(
        err instanceof Error ? err.message : "Failed to create Forge project"
      );
    } finally {
      setCreatingProject(false);
    }
  };

  const resetForm = () => {
    setClient("");
    setInvoiceLink("");
    setInvoiceTotal("");
    setPaidToDate("");
    setPaymentProcessorFee("0");
    setLoanRepayment("0");
    setIncomeType("Actual");
    setRecurrencePattern("");
    setRecurrenceDays([]);
    setExpectedByDate("");
    setReceivedDate("");
    setDepositDate("");
    setDependsOnCompletion("");
    setNotes("");
    setProject("");
    setForgeOrganizationId("");
    setForgeProjectId("");
    setForgeProjects([]);
    setSelectedDocId("");
    setSourceProvider("");
    setSourceDocumentType("");
    setSourceDocumentId("");
  };

  const formatRecurrenceLabel = (pattern: string) => {
    if (!pattern) return "one-time";
    if (pattern.startsWith("daily:")) {
      const dayMap: Record<string, string> = {
        mon: "M",
        tue: "T",
        wed: "We",
        thu: "Thu",
        fri: "Fri",
        sat: "Sat",
        sun: "Sun",
      };
      return `daily (${pattern
        .replace("daily:", "")
        .split(",")
        .filter(Boolean)
        .map((day) => dayMap[day] || day)
        .join(", ")})`;
    }
    return pattern;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedReceivedDate = parseDateInput(receivedDate);
    const parsedDepositDate = parseDateInput(depositDate);
    const isDailyRecurrence = recurrencePattern === DAILY_RECURRENCE_VALUE;
    // Daily recurrence schedules a new occurrence every selected weekday, so an
    // explicit "Expected by Date" is not required — default it to today.
    const expectedDate = expectedByDate?.trim()
      ? new Date(expectedByDate)
      : parsedReceivedDate || (isDailyRecurrence ? new Date() : null);
    const isRecurring = recurrencePattern !== "";

    if (!clientTrimmed || !invoiceTotal) {
      setError("Please fill in all required fields");
      return;
    }
    if (!expectedDate) {
      setError("Expected By Date or Date Received is required");
      return;
    }
    if (
      recurrencePattern === DAILY_RECURRENCE_VALUE &&
      recurrenceDays.length === 0
    ) {
      setError("Please select at least one day for daily recurrence");
      return;
    }

    const total = parseFloat(invoiceTotal);
    const paid = parseFloat(paidToDate) || 0;
    const processorFee = parseFloat(paymentProcessorFee) || 0;
    const loanRepaymentValue = parseFloat(loanRepayment) || 0;

    if (Number.isNaN(total)) {
      setError("Invoice Total must be a valid number");
      return;
    }
    if (paid > total) {
      setError("Paid to Date cannot be greater than Invoice Total");
      return;
    }
    if (processorFee + loanRepaymentValue > total) {
      setError(
        "Payment Processor Fee + Loan Repayment cannot exceed Invoice Total"
      );
      return;
    }

    const resolvedRecurrencePattern =
      recurrencePattern === DAILY_RECURRENCE_VALUE
        ? `daily:${recurrenceDays.join(",")}`
        : recurrencePattern;

    const incomePayload: Omit<Income, "id"> = {
      client: clientTrimmed,
      invoiceLink: invoiceLink.trim() || undefined,
      invoiceTotal: total,
      paidToDate: paid,
      expectedByDate: expectedDate,
      type: incomeType,
      dependsOnCompletion: dependsOnCompletion.trim() || undefined,
      isPaid: Boolean(parsedReceivedDate) || (incomeToEdit?.isPaid ?? false),
      isRecurring,
      recurrencePattern: isRecurring ? resolvedRecurrencePattern : undefined,
      notes: notes.trim() || undefined,
      receivedDate: parsedReceivedDate,
      depositDate: parsedDepositDate,
      paymentProcessorFee: processorFee,
      loanRepayment: loanRepaymentValue,
      project: project.trim() || undefined,
      forgeOrganizationId: forgeOrganizationId || undefined,
      forgeProjectId: forgeProjectId || undefined,
      sourceProvider: sourceProvider || undefined,
      sourceDocumentType: sourceDocumentType || undefined,
      sourceDocumentId: sourceDocumentId || undefined,
    };

    setError("");
    setIsSubmitting(true);

    try {
      if (clientTrimmed) {
        persistClientOptions([...clientOptions, clientTrimmed]);
      }

      if (isEditing && incomeToEdit && onUpdateIncome) {
        const previousPattern = initialRecurrencePatternRef.current || "";
        const nextPattern = isRecurring ? resolvedRecurrencePattern || "" : "";
        if (previousPattern !== nextPattern) {
          const confirmed = window.confirm(
            `Changing frequency from "${formatRecurrenceLabel(
              previousPattern
            )}" to "${formatRecurrenceLabel(
              nextPattern
            )}" updates the series from today forward. Proceed?`
          );
          if (!confirmed) return;
        }

        await Promise.resolve(
          onUpdateIncome({
            ...incomePayload,
            id: incomeToEdit.id,
          })
        );
      } else {
        await Promise.resolve(onAddIncome(incomePayload));
        resetForm();
      }
    } catch (submitError) {
      console.error("[IncomeForm] Failed to save income:", submitError);
      setError("Failed to save income. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const netDeposited =
    (parseFloat(invoiceTotal) || 0) -
    (parseFloat(paymentProcessorFee) || 0) -
    (parseFloat(loanRepayment) || 0);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "grid", gap: 1.25 }}
    >
      {error ? (
        <Box sx={{ color: "error.main", fontSize: 14, mb: 1 }}>{error}</Box>
      ) : null}

      {incomeToEdit?.createdVia && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CreatedViaBadge createdVia={incomeToEdit.createdVia} />
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 1,
          alignItems: "end",
        }}
      >
        <Autocomplete
          freeSolo
          options={clientSelectOptions}
          value={client}
          onInputChange={(_event, value) => setClient(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client Name *"
              placeholder={
                forgeConnected
                  ? "Search clients (incl. Forge organizations)"
                  : "Type to search client"
              }
              size="small"
              fullWidth
            />
          )}
        />
        <Tooltip
          title={canAddClient ? "Add new client" : "Type a new client name"}
        >
          <span>
            <IconButton
              color="primary"
              onClick={handleAddClient}
              disabled={!canAddClient}
              sx={{ mb: 0.5 }}
            >
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 1,
          alignItems: "end",
        }}
      >
        <Autocomplete
          freeSolo
          // When a Forge client/org is chosen, scope to its projects; otherwise
          // search every Forge project so the user can type to find one directly.
          options={(forgeOrganizationId ? forgeProjects : allForgeProjects).map(
            (p) => p.name
          )}
          value={project}
          onInputChange={(_event, value) => {
            setProject(value);
            const source = forgeOrganizationId
              ? forgeProjects
              : allForgeProjects;
            const match = source.find(
              (p) => p.name.toLowerCase() === value.trim().toLowerCase()
            );
            setForgeProjectId(match ? match.id : "");
            // If the matched project belongs to a Forge org and no client is set
            // yet, auto-fill the client/org from the project.
            if (match && match.organizationId && !forgeOrganizationId) {
              const org = forgeOrgs.find((o) => o.id === match.organizationId);
              if (org) {
                setForgeOrganizationId(org.id);
                setClient(org.name);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project"
              placeholder={
                forgeOrganizationId
                  ? "Search Forge projects for this client"
                  : forgeConnected
                    ? "Search Forge projects"
                    : "Project name"
              }
              size="small"
              fullWidth
            />
          )}
        />
        <Tooltip
          title={
            !forgeOrganizationId
              ? "Pick a Forge client first to create a project"
              : canAddProject
                ? `Create "${projectTrimmed}" in Focus: Forge`
                : "Type a new project name"
          }
        >
          <span>
            <IconButton
              color="primary"
              onClick={handleAddProject}
              disabled={!canAddProject || creatingProject}
              sx={{ mb: 0.5 }}
            >
              {creatingProject ? <CircularProgress size={20} /> : <AddIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <TextField
        select
        label="Type"
        size="small"
        fullWidth
        value={incomeType}
        onChange={(e) =>
          setIncomeType(e.target.value as "Actual" | "Prospective")
        }
      >
        <MenuItem value="Actual">Actual</MenuItem>
        <MenuItem value="Prospective">Prospective</MenuItem>
      </TextField>

      <TextField
        select
        label="Recurrence Pattern"
        size="small"
        fullWidth
        value={recurrencePattern}
        onChange={(e) => {
          const next = e.target.value;
          setRecurrencePattern(next);
          if (next !== DAILY_RECURRENCE_VALUE) setRecurrenceDays([]);
        }}
      >
        {recurrenceOptions.map((option) => (
          <MenuItem key={option.value || "one-time"} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {recurrencePattern === DAILY_RECURRENCE_VALUE ? (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            px: 1,
            py: 0.75,
          }}
        >
          <Box sx={{ fontSize: 12, color: "text.secondary", mb: 0.25 }}>
            Days of Week
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {recurrenceDayOptions.map((day) => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    size="small"
                    checked={recurrenceDays.includes(day.value)}
                    onChange={() => {
                      setRecurrenceDays((current) =>
                        current.includes(day.value)
                          ? current.filter((value) => value !== day.value)
                          : [...current, day.value]
                      );
                    }}
                  />
                }
                label={day.label}
                sx={{ mr: 0.5 }}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      <TextField
        type="date"
        label={`Expected by Date${
          !receivedDate.trim() && recurrencePattern !== DAILY_RECURRENCE_VALUE
            ? " *"
            : ""
        }`}
        size="small"
        fullWidth
        value={expectedByDate}
        onChange={(e) => setExpectedByDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        helperText={
          receivedDate.trim() ? "Optional when Date Received is set." : " "
        }
      />

      <TextField
        type="datetime-local"
        label="Date Received"
        size="small"
        fullWidth
        value={receivedDate}
        onChange={(e) =>
          setReceivedDate(normalizeDateTimeInput(e.target.value))
        }
        InputLabelProps={{ shrink: true }}
      />

      <Box>
        <TextField
          type="datetime-local"
          label="Deposit Date"
          size="small"
          fullWidth
          value={depositDate}
          onChange={(e) =>
            setDepositDate(normalizeDateTimeInput(e.target.value))
          }
          InputLabelProps={{ shrink: true }}
        />
        <Box
          sx={{
            fontSize: 12,
            color: "text.secondary",
            mt: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Tooltip
            title="The date the received funds are expected to deposit."
            arrow
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          Auto net deposited:{" "}
          {netDeposited.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </Box>
      </Box>

      {tpcConnected && billingDocs.length > 0 && (
        <TextField
          select
          label="Select Proposal or Invoice"
          size="small"
          fullWidth
          value={selectedDocId}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedDocId(id);
            const doc = billingDocs.find((d) => d.id === id);
            if (!doc) {
              setSourceProvider("");
              setSourceDocumentType("");
              setSourceDocumentId("");
              return;
            }
            setInvoiceTotal(String(doc.total ?? ""));
            setSourceProvider("tpc");
            setSourceDocumentType(doc.type);
            setSourceDocumentId(doc.id);
            if (doc.clientName && !client.trim()) setClient(doc.clientName);
          }}
          helperText="Populates Invoice Total from your TPC proposals & invoices"
        >
          <MenuItem value="">None</MenuItem>
          {billingDocs.map((doc) => (
            <MenuItem key={doc.id} value={doc.id}>
              {doc.type === "invoice" ? "Invoice" : "Proposal"}: {doc.label}
              {doc.clientName ? ` — ${doc.clientName}` : ""} (
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(doc.total || 0)}
              )
            </MenuItem>
          ))}
        </TextField>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1,
        }}
      >
        <TextField
          type="number"
          label="Invoice Total *"
          size="small"
          inputProps={{ min: 0, step: 0.01 }}
          value={invoiceTotal}
          onChange={(e) => setInvoiceTotal(e.target.value)}
          fullWidth
        />
        <TextField
          type="number"
          label="Paid to Date"
          size="small"
          inputProps={{ min: 0, step: 0.01 }}
          value={paidToDate}
          onChange={(e) => setPaidToDate(e.target.value)}
          fullWidth
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1,
        }}
      >
        <TextField
          type="number"
          label="Payment Processor Fee"
          size="small"
          inputProps={{ min: 0, step: 0.01 }}
          value={paymentProcessorFee}
          onChange={(e) => setPaymentProcessorFee(e.target.value)}
          fullWidth
        />
        <TextField
          type="number"
          label="Loan Repayment"
          size="small"
          inputProps={{ min: 0, step: 0.01 }}
          value={loanRepayment}
          onChange={(e) => setLoanRepayment(e.target.value)}
          fullWidth
        />
      </Box>

      <TextField
        label="Depends on Completion of"
        size="small"
        fullWidth
        value={dependsOnCompletion}
        onChange={(e) => setDependsOnCompletion(e.target.value)}
      />

      <TextField
        type="url"
        label="Link to Invoice"
        size="small"
        fullWidth
        value={invoiceLink}
        onChange={(e) => setInvoiceLink(e.target.value)}
        placeholder="https://..."
      />

      <TextField
        label="Notes"
        size="small"
        fullWidth
        multiline
        minRows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <Button
        type="submit"
        variant="contained"
        disabled={isSubmitting}
        sx={{ mt: 1, py: 1.1 }}
      >
        {isSubmitting ? (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} sx={{ color: "inherit" }} />
            Saving...
          </Box>
        ) : isEditing ? (
          "Update Income"
        ) : (
          "Add Income"
        )}
      </Button>
    </Box>
  );
};

export default IncomeForm;
